import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, UploadCloud, ClipboardPaste, Pencil, Trash2, RefreshCw, Save, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { formatLocalDateTime } from "../lib/datetime";

const MAX_CHARS = 8000;
const PREVIEW_CHARS = 800;
const ACCEPT = ".pdf,.docx,.rtf,.txt,.md,.html";

/** Org Social Media Policy — hard rules injected into every Social Queue draft. */
export default function SocialPolicyPanel({ orgId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showFull, setShowFull] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/orgs/${orgId}/social/policy`)
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load policy"));
  }, [orgId]);

  const run = async (fn, okMsg) => {
    setBusy(true); setError("");
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(async () => {
      const { data: d } = await api.post(`/orgs/${orgId}/social/policy/upload`, fd);
      setData(d); setEditing(false);
    }, "Policy uploaded — drafts will follow it");
  };

  const save = () => run(async () => {
    const { data: d } = await api.put(`/orgs/${orgId}/social/policy`, { text: draft });
    setData(d); setEditing(false);
  }, "Policy saved");

  const remove = () => run(async () => {
    await api.delete(`/orgs/${orgId}/social/policy`);
    setData({ configured: false, text: "" }); setShowFull(false);
  }, "Policy removed");

  const startEdit = () => { setDraft(data?.text || ""); setEditing(true); setError(""); };
  const configured = !!data?.configured;
  const text = data?.text || "";

  return (
    <section id="social-policy" className="sp-glass-strong rounded-2xl p-6 mb-8" data-testid="social-policy-panel">
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="social-policy-file-input" />
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-white/80" />
            <h3 className="text-sm uppercase tracking-widest sp-subtle">Social Media Policy</h3>
            {configured && <span className="sp-chip text-[10px]" data-testid="social-policy-status">Active</span>}
          </div>
          {!configured && (
            <p className="text-xs sp-subtle max-w-2xl" data-testid="social-policy-empty">
              Upload your social media policy and every post we draft will follow it.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {busy && <Loader2 className="w-4 h-4 animate-spin sp-subtle" data-testid="social-policy-busy" />}
          {!editing && (
            <>
              <button onClick={() => fileRef.current?.click()} disabled={busy} className="sp-btn sp-btn-ghost text-xs disabled:opacity-50" data-testid={configured ? "social-policy-replace-btn" : "social-policy-upload-btn"}>
                {configured ? <><RefreshCw className="w-3.5 h-3.5" /> Replace</> : <><UploadCloud className="w-3.5 h-3.5" /> Upload document</>}
              </button>
              <button onClick={startEdit} disabled={busy} className="sp-btn sp-btn-ghost text-xs disabled:opacity-50" data-testid={configured ? "social-policy-edit-btn" : "social-policy-paste-btn"}>
                {configured ? <><Pencil className="w-3.5 h-3.5" /> Edit</> : <><ClipboardPaste className="w-3.5 h-3.5" /> Paste policy</>}
              </button>
              {configured && (
                <button onClick={() => setConfirmOpen(true)} disabled={busy} className="sp-btn sp-btn-ghost text-xs hover:text-red-300 disabled:opacity-50" data-testid="social-policy-remove-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-300 mb-3" data-testid="social-policy-error">{error}</div>}

      {editing ? (
        <div className="sp-glass rounded-xl p-3" data-testid="social-policy-editor">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={MAX_CHARS}
            rows={10}
            placeholder="Paste your social media policy — e.g. Never use emojis. Never mention competitors."
            className="w-full bg-transparent text-[13px] leading-relaxed placeholder:opacity-40 focus:outline-none resize-y"
            data-testid="social-policy-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] sp-subtle" data-testid="social-policy-char-counter">{draft.length} / {MAX_CHARS}</span>
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="sp-btn sp-btn-ghost text-xs" data-testid="social-policy-cancel-btn">Cancel</button>
              <button onClick={save} disabled={busy || !draft.trim()} className="sp-btn sp-btn-primary text-xs disabled:opacity-50" data-testid="social-policy-save-btn">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      ) : configured && (
        <div className="sp-glass rounded-xl p-3" data-testid="social-policy-summary">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] sp-subtle mb-2">
            <span data-testid="social-policy-filename">{data.filename || "Pasted policy"}</span>
            <span data-testid="social-policy-char-count">{(data.char_count || text.length).toLocaleString()} chars</span>
            {data.updated_at && <span data-testid="social-policy-updated">Updated {formatLocalDateTime(data.updated_at, { month: "short", day: "numeric", year: "numeric" })}</span>}
          </div>
          <div className="text-[13px] leading-relaxed whitespace-pre-wrap opacity-90" data-testid="social-policy-preview">
            {showFull || text.length <= PREVIEW_CHARS ? text : `${text.slice(0, PREVIEW_CHARS)}…`}
          </div>
          {text.length > PREVIEW_CHARS && (
            <button onClick={() => setShowFull((v) => !v)} className="text-[11px] sp-subtle hover:text-white mt-2" data-testid="social-policy-show-full-btn">
              {showFull ? "Show less" : "Show full"}
            </button>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="social-policy-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove social media policy?</AlertDialogTitle>
            <AlertDialogDescription>New drafts will no longer be checked against these rules.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="social-policy-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="social-policy-remove-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
